import { Tooltip } from "antd";
import useAutosizerScrollStatus from "hooks/useAutosizerScrollStatus";
import { useMemo, useRef, MouseEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import {
  METADATA_SOURCES_PARAMETER,
  PAGE_PARAMETER,
} from "helpers/constants/miscellaneous";
import CheckIcon from "assets/icons/CheckIcon";
import VendorIcon from "assets/icons/VendorIcon";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { clearFilters } from "redux/SingleDataset/actions";
import AddEnrichmentIcon from "assets/icons/AddEnrichmentIcon";
import { useAccordionContext } from "contexts/AccordionContext";
import { useLocation, useNavigate, useParams } from "react-router";
import { getDataset } from "redux/SingleDataset/selectors";
import {
  ModelTypes,
  FilterOption,
  MetadataSource,
  TrackingFilterTypes,
  FilterItemCountsWithSelect,
  DatasetStatusEnum,
  EDatasetStatus,
} from "types";
import ContentBodyLoader from "../ContentBodyLoader";
import AnimatedBar from "../AnimatedSingleBar";
import ModelBadge from "./ModelBadge";
import styles from "./style.module.scss";
import VLPopover from "views/uikit/VLPopover";
import { getDisabledEnrichmentMessageByDatasetStatus } from "views/pages/DataPage/CardsView/DataList/EnrichmentCard/helpers";
import { useFeatureFlagService } from "../../../../../../hooks/useFeatureFlagService";
import { NEW_STATUS } from "../../../../../../helpers/constants/filters";

const MARGIN_TOP = 16;
const HEADER_HEIGHT = 32;
const HEADER_PADDING = 16;

const NoMatchingData = () => (
  <div className={styles.noMatchcontainer}>No Matching Data.</div>
);

const AddEnrichmentButton = ({
  onClick,
  disabledByDeprecatedStatus,
  disabledByNewStatus,
}: {
  onClick: () => void;
  disabledByDeprecatedStatus?: DatasetStatusEnum;
  disabledByNewStatus?: EDatasetStatus;
}) => {
  const displayNewStatus = useFeatureFlagService(NEW_STATUS).show;

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    onClick();
  };

  const shouldDisable = displayNewStatus
    ? !!disabledByNewStatus
    : !!disabledByDeprecatedStatus;

  const getTooltipTitle = () => {
    if (displayNewStatus && disabledByNewStatus) {
      return (
        <div className={styles.disabledEnrichment}>
          Enrichment is disabled for {disabledByNewStatus} datasets.
        </div>
      );
    } else if (!displayNewStatus && disabledByDeprecatedStatus) {
      return getDisabledEnrichmentMessageByDatasetStatus(
        disabledByDeprecatedStatus,
      );
    }
    return undefined;
  };

  return (
    <VLPopover content={getTooltipTitle()}>
      <div
        className={`${styles.addEnrichmentButton} ${shouldDisable ? styles.disabled : ""}`}
        onClick={!shouldDisable ? handleClick : undefined}
      >
        <AddEnrichmentIcon />
        <span>Add more models</span>
      </div>
    </VLPopover>
  );
};

const Row = ({
  data,
  index,
  style,
  isLoading,
  showCheckIcon,
  isUnselectable,
  handleSingleSelection,
}: any) => {
  const { title, iconPath = "", selected, type = null } = data[index];

  const pt = index === 0 || index === data.length - 1 ? "1px" : "0";

  const handleBarClick = () => {
    if (isUnselectable) {
      return;
    }
    handleSingleSelection(index, !selected);
  };

  return (
    <div
      className={styles.singleBarRow}
      style={{ ...style, padding: `${pt} 16px 8px 16px` }}
    >
      <SingleBar
        title={title}
        modelType={type}
        iconPath={iconPath}
        isSelected={selected}
        isLoading={isLoading}
        showCheckIcon={showCheckIcon}
        isUnselectable={isUnselectable}
        onBarClick={handleBarClick}
      />
    </div>
  );
};

const SingleBar = ({
  title,
  iconPath,
  modelType,
  isLoading,
  isSelected,
  showCheckIcon,
  isUnselectable,
  onBarClick,
}: SingleBarProps) => {
  const singleBarClassnames = classNames(styles.singleBarDisplay, {
    [styles.unSelectableBar]: isUnselectable,
  });

  const checkBoxClassnames = classNames(styles.checkBox, {
    [styles.blueBG]: isSelected,
  });

  const renderIcon = () => {
    if (!iconPath) {
      return <noscript />;
    }

    return (
      <div className={styles.icon}>
        <VendorIcon uri={iconPath} />
        {modelType && <ModelBadge modelType={modelType} />}
      </div>
    );
  };

  return (
    <AnimatedBar className={singleBarClassnames}>
      <Tooltip
        title={
          isUnselectable
            ? "Not compatible with the current selection. Clear the selection to choose it"
            : undefined
        }
        mouseEnterDelay={0.4}
      >
        <div className={styles.barContainer} onClick={onBarClick}>
          {renderIcon()}

          <div className={styles.barTitle} title={title}>
            {title}
          </div>

          {!isLoading && showCheckIcon && (
            <div className={checkBoxClassnames}>
              {isSelected && <CheckIcon variant="flat" />}
            </div>
          )}
          {isLoading && <ContentBodyLoader />}
        </div>
      </Tooltip>
    </AnimatedBar>
  );
};

const MultipleSelectionsContent = ({
  data,
  sources,
  isLoading,
  contentType,
  searchDisabled,
  showNoMatchingData,
  isContentSelected,
  entityTypeFilter,
  trackMetadataFilterChange,
  getIsUnselectable,
}: MultipleSelectionsContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<any>(null);
  const { datasetId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setHeightLimits, height: contextHeight } = useAccordionContext();
  const [calculatedHeight, setCalculatedHeight] = useState(contextHeight);
  const dataset = useSelector(getDataset);
  const datasetStatus: DatasetStatusEnum = dataset?.status;
  const datasetNewStatus: EDatasetStatus = dataset?.status_new;
  const selectedSourceIds = useMemo(
    () =>
      sources.filter((source) => source.selected).map((source) => source.id),
    [sources],
  );

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const theme = useMemo(() => localStorage.getItem("theme") || "", []);
  const showAddEnrichmentButton = false;

  const extraHeight = searchDisabled
    ? MARGIN_TOP
    : MARGIN_TOP + HEADER_HEIGHT + HEADER_PADDING;

  const { isOverflowing, isAtBottom } = useAutosizerScrollStatus(
    containerRef,
    listRef,
    { dependencyList: [data.length, isContentSelected] },
  );

  // Calculate heights for the accordion
  const calculateHeight = (numBars: number) => {
    const singleBarHeight = itemSize();
    return extraHeight + numBars * singleBarHeight;
  };

  useEffect(() => setCalculatedHeight(contextHeight), [contextHeight]);

  useEffect(() => {
    // Update accordion height limits when they change
    const initialHeight = calculateHeight(Math.min(data.length, 5));
    const minHeight = calculateHeight(Math.min(data.length, 2));
    const maxHeight = calculateHeight(data.length);

    if (
      calculatedHeight &&
      calculatedHeight >= minHeight &&
      calculatedHeight <= maxHeight
    ) {
      return;
    }

    setHeightLimits({ initialHeight, minHeight, maxHeight });
    //eslint-disable-next-line
  }, [data, searchDisabled, showAddEnrichmentButton]);

  const handleSingleSelection = (index: number) => {
    let trackingType: TrackingFilterTypes | null = null;

    //track label filter selection for amplitude
    trackMetadataFilterChange(trackingType, entityTypeFilter, "Added");

    const existingIndex = selectedSourceIds.indexOf(data[index].id!);
    if (existingIndex > -1) {
      selectedSourceIds.splice(existingIndex, 1);
    } else {
      selectedSourceIds.push(data[index].id!);
    }

    if (selectedSourceIds.length < sources.length) {
      searchParams.set(
        METADATA_SOURCES_PARAMETER,
        JSON.stringify(selectedSourceIds),
      );
    } else {
      searchParams.delete(METADATA_SOURCES_PARAMETER);
    }

    searchParams.set(PAGE_PARAMETER, "1");
    navigate({ search: `?${searchParams.toString()}` });
  };

  const handleEnrichmentClick = () => {
    dispatch(clearFilters() as any);
    navigate(`/dataset/${datasetId!}/enrich`);
  };

  const itemSize = () => {
    return 48; // Set a default item height
  };

  const dynamicWrapperHeight = () => {
    if (isContentSelected) {
      return { height: "100%", maxHeight: "100%" };
    } else if (calculatedHeight) {
      return { height: `${calculatedHeight - extraHeight}px` };
    }

    const fallback = calculateHeight(Math.min(data.length, 5));
    return { height: `${fallback - extraHeight}px` };
  };

  const renderRow = ({ index, style }: any) => {
    const singleData = data[index];
    const isUnselectable = getIsUnselectable
      ? getIsUnselectable(singleData)
      : false;

    const showCheckIcon = singleData.id
      ? singleData.isSelectable || contentType === "sources"
      : false;

    return (
      <Row
        key={index}
        data={data}
        index={index}
        style={style}
        isLoading={isLoading}
        showCheckIcon={showCheckIcon}
        isUnselectable={isUnselectable}
        handleSingleSelection={handleSingleSelection}
      />
    );
  };

  if (showNoMatchingData) {
    return <NoMatchingData />;
  }

  return (
    <>
      <div
        ref={containerRef}
        className={classNames(styles.wrapper, {
          [styles.overflowingWrapper]: isOverflowing && !isAtBottom,
          [styles.modelsWrapper]: contentType === "models" && !!data[0]?.type,
        })}
        style={dynamicWrapperHeight()}
      >
        <AutoSizer>
          {({ height, width }: any) => (
            <List
              innerRef={listRef}
              outerRef={containerRef}
              width={width}
              height={height}
              itemData={data}
              itemSize={itemSize}
              itemCount={data.length}
              className={`List ${styles.barListContainer}`}
              key={contentType + theme}
            >
              {renderRow}
            </List>
          )}
        </AutoSizer>
      </div>
      {showAddEnrichmentButton && (
        <AddEnrichmentButton
          onClick={handleEnrichmentClick}
          disabledByDeprecatedStatus={
            datasetStatus === "UPDATING" || datasetStatus === "PENDING_INDEX"
              ? datasetStatus
              : undefined
          }
          disabledByNewStatus={
            datasetNewStatus === EDatasetStatus.INDEXING ||
            datasetNewStatus === EDatasetStatus.READ_ONLY
              ? datasetNewStatus
              : undefined
          }
        />
      )}
    </>
  );
};

export default MultipleSelectionsContent;

interface MultipleSelectionsContentProps {
  data: MetadataSource[];
  showNoMatchingData: any;
  isContentSelected: any;
  entityTypeFilter: FilterOption[];
  resetSection: any;
  isLoading: boolean;
  searchDisabled: boolean;
  updateQueryParameter: any;
  trackMetadataFilterChange: any;
  contentType: "sources" | "models";
  sources: MetadataSource[];
  getIsUnselectable?: (value: FilterItemCountsWithSelect) => boolean;
}

interface SingleBarProps {
  title: string;
  iconPath: string;
  isLoading: boolean;
  isSelected: boolean;
  showCheckIcon: boolean;
  isUnselectable: boolean;
  modelType: ModelTypes | null;
  onBarClick: () => void;
}
